import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { PaymentChannelModel } from 'api-hooks/payment-channel';
import { NavigationRoutes } from 'common/constants';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { SeparatorVertical } from 'components/styles';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import Input from 'mantine-components/elements';
import { HalfGridContainer } from 'mantine-components/container/grid-container';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UniskaiFormHeaderRef } from 'templates/form/uniskai-form-header';
import UniskaiFormTemplate from 'templates/form/uniskai-form-template';

import { PaymentChannelFormSchema, PaymentChannelFormType } from './form-type';
import usePaymentChannelDefaultValues from '../hooks/use-payment-channel-default-values';

interface PaymentChannelFormProps {
  query?: UseQueryResult<ApiResult<PaymentChannelModel>, ApiError>;
  onSubmit: (
    input: PaymentChannelFormType,
    form: UseFormReturn<PaymentChannelFormType>,
  ) => Promise<PaymentChannelModel>;
  saveMutation?: UseMutationResult;
}

export default function PaymentChannelForm(props: PaymentChannelFormProps) {
  const { t } = useTranslation();
  const paymentChannel = props.query?.data?.data;
  const defaultValues = usePaymentChannelDefaultValues(paymentChannel);
  const { deleteMutate, onDelete } = useDestroyAction('payment-channels');
  const permission = useGetCrudPermission('payment-channels');
  const titleRef = React.useRef<UniskaiFormHeaderRef>(null);

  React.useEffect(() => {
    let title = t(`pathname:${NavigationRoutes.PaymentChannelsCreate}`);
    if (paymentChannel) {
      title = t(`pathname:${NavigationRoutes.PaymentChannelsView}`, {
        extra: paymentChannel.name,
      });
    }
    titleRef.current?.setTitle(title);
  }, [paymentChannel, t]);

  return (
    <UniskaiFormTemplate
      formProps={{
        defaultEditable: !paymentChannel,
        defaultValues,
        resolver: yupResolver(PaymentChannelFormSchema()),
        onSubmit: props.onSubmit as any,
      }}
      customs={[
        {
          type: 'delete',
          loading: deleteMutate.isLoading,
          onClick: () => onDelete(paymentChannel?.id),
          show: permission.delete,
        },
        {
          type: 'edit',
          show: permission.update,
        },
      ]}
      data={paymentChannel}
      query={props.query}
      titleRef={titleRef}
      saveMutation={props.saveMutation}
    >
      <HalfGridContainer>
        <Input
          type="text"
          name="name"
          label={t('common:name')}
          placeholder={t('common:name')}
          required
        />
      </HalfGridContainer>
      <SeparatorVertical $margin={16} />
      <HalfGridContainer>
        <Input
          type="textarea"
          name="description"
          label={t('common:description')}
          placeholder={t('common:description')}
        />
      </HalfGridContainer>
      <SeparatorVertical $margin={20} />
      <HalfGridContainer>
        <Input type="checkbox" name="isActive" label={t('common:is_active')} />
      </HalfGridContainer>
    </UniskaiFormTemplate>
  );
}
